import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NgxTurnstileModule } from 'ngx-turnstile';
import { Subscription, catchError, finalize, of, take, tap } from 'rxjs';
import { AfmBeService } from 'src/app/services/afm-be/afm-be.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { SubsectionComponent } from 'src/app/shared/components/subsection/subsection.component';
import { Stripe } from 'stripe';

@Component({
  selector: 'app-support-us',
  imports: [CommonModule, RouterModule, AsyncPipe, MatListModule, MatIconModule, MatCardModule, MatButtonModule, MatExpansionModule, MatTooltipModule, MatProgressBarModule, NgxTurnstileModule, SectionComponent, SubsectionComponent],
  templateUrl: './support-us.component.html',
  styleUrl: './support-us.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportUsComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  userMail: string = '';

  isLoadingAfmUser = signal<boolean>(false);
  isLoadingCheckout = signal<boolean>(false);
  isLoadingPortal = signal<boolean>(false);
  showTurnstileOverlay = signal<boolean>(false);
  pendingAction: ((token: string) => void) | null = null;

  turnstileSiteKey = '0x4AAAAAAA177F80eXAbXgwO';

  isLoading = computed(() => {
    return (this.userSettingsService.isLoggedIn ? this.isLoadingAfmUser() : false) || this.afmbe.isLoadingProducts() || this.isLoadingCheckout() || this.isLoadingPortal() || this.afmbe.isLoadingUserData();
  });
  activeSubscriptionsProductIds = computed(() => {
    return this.afmbe
      .afmStripeUserData()
      .activeSubscriptions.flatMap((sub) => sub.items.data)
      .map((item) => item.plan.product as string);
  });

  constructor(
    public afmbe: AfmBeService,
    public authService: AuthService,
    public userSettingsService: UserSettingsService,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef,
    private loadingBarService: LoadingBarService
  ) {}
  ngOnInit(): void {
    this.subscriptions.push(
      this.userSettingsService.afmUser$
        .pipe(
          tap(() => {
            this.isLoadingAfmUser.set(true);
            this.cdr.markForCheck();
          }),
          catchError((error) => {
            console.error('Error fetching user:', error);
            return of(null);
          }),
          finalize(() => {
            this.isLoadingAfmUser.set(false);
            this.cdr.markForCheck();
          }),
          take(1)
        )
        .subscribe((user) => {
          this.userMail = user?.email ?? '';
          this.cdr.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onTurnstileResponse(response: string | null) {
    if (response && this.pendingAction) {
      this.pendingAction(response);
      this.pendingAction = null;
    }
    setTimeout(() => {
      this.showTurnstileOverlay.set(false);
      this.cdr.markForCheck();
    }, 1000);
  }

  onTurnstileError(errorCode: string | null) {
    console.error('Turnstile error:', errorCode);
    setTimeout(() => {
      this.showTurnstileOverlay.set(false);
      this.pendingAction = null;
      this.cdr.markForCheck();
    }, 1500);
  }

  withTurnstileVerification(action: (token: string) => void): void {
    this.showTurnstileOverlay.set(true);
    this.pendingAction = action;
    this.cdr.markForCheck();
  }

  async createCheckout(price: Stripe.Price, productName: string): Promise<void> {
    this.withTurnstileVerification((token) => {
      this.isLoadingCheckout.set(true);
      this.cdr.markForCheck();
      try {
        this.analyticsService.sendBeginCheckoutEvent(price.currency.toUpperCase() ?? '', (price.unit_amount ?? 0) / 100, [{ item_id: price.id, item_name: productName }]);
        this.initiateCheckout(price.id, token);
        this.cdr.markForCheck();
      } catch (error) {
        console.error('An error occurred:', error);
        this.isLoadingCheckout.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  private initiateCheckout(priceId: string, turnstileToken: string): void {
    this.afmbe.createCheckout(priceId, turnstileToken).subscribe({
      next: (session) => {
        window.location.href = session.url ?? '';
      },
      error: (err) => {
        console.error(err);
        this.isLoadingCheckout.set(false);
        this.cdr.markForCheck();
      },
      complete: () => {
        this.cdr.markForCheck();
      },
    });
  }

  getPricesIds(prices: Stripe.Price[]): string[] {
    this.cdr.markForCheck();
    return prices.map((price) => price.id);
  }

  getPortalSession(customerId: string): void {
    this.withTurnstileVerification((token) => {
      this.isLoadingPortal.set(true);
      this.cdr.markForCheck();
      this.afmbe.createPortalSession(customerId, token).subscribe({
        next: (session) => {
          window.location.href = session.url ?? '';
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error(err);
          this.isLoadingPortal.set(true);
          this.cdr.markForCheck();
        },
      });
    });
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0] + '.')
      .join(' ')
      .toUpperCase();
  }

  privatizeEmail(email: string): string {
    let [name, domain] = email.split('@');
    return `${name[0]}***${name[name.length - 1]}@${domain}`;
  }
  asCustomer(value: string | Stripe.Customer | Stripe.DeletedCustomer): Stripe.Customer | null {
    if (typeof value !== 'string' && 'id' in value) {
      return value as Stripe.Customer;
    }
    return null;
  }

  checkDiscount(prod: Stripe.Product): boolean {
    return prod.marketing_features.some((f) => f.name?.includes('OFF'));
  }

  getDiscountText(prod: Stripe.Product): string {
    const discountFeature = prod.marketing_features.find((f) => f.name?.includes('OFF'))?.name ?? '';
    const percentMatch = discountFeature.match(/(\d+%)\s*(?:OFF)/i);
    return percentMatch ? `${percentMatch[1]} OFF` : '';
  }
}
